/**
 * Image-file upload helpers — most importantly HEIC/HEIF normalization.
 *
 * iPhone camera photos are HEIC, which `accept="image/*"` happily admits but
 * almost no browser can RENDER (broken <img> everywhere except Safari). Policy:
 * we ALLOW the upload and convert to JPEG client-side before storing, so the
 * stored file displays for every downstream viewer (client review, admin,
 * designers) with zero per-render conversion cost.
 *
 * `heic2any` is imported dynamically so the (~300KB) codec only loads when a
 * HEIC file is actually picked.
 */

import imageCompression from "browser-image-compression";

const HEIC_NAME_RE = /\.(heic|heif)$/i;
const RASTER_NAME_RE = /\.(jpe?g|png|webp)$/i;

export const isHeicFile = (file: File): boolean =>
  HEIC_NAME_RE.test(file.name) ||
  file.type === "image/heic" ||
  file.type === "image/heif";

const isRasterImage = (file: File): boolean =>
  RASTER_NAME_RE.test(file.name) || /^image\/(jpe?g|png|webp)$/i.test(file.type);

interface DownscaleOptions {
  /** Longest-edge cap in px. High-res originals (4000px phone/designer files)
   *  shrink to this before upload; smaller images pass through unchanged. */
  maxDimension?: number;
  /** Soft target size before quality backs off. */
  maxSizeMB?: number;
  quality?: number;
}

/**
 * Downscale + recompress a large raster image BEFORE upload, so a 64px thumbnail
 * is never backed by a multi-MB 4000px original. Plan-independent (runs in the
 * browser via a web worker) — this is the Free-plan win that complements the
 * Pro-only display-time CDN transforms (see lib/storageImage.ts).
 *
 * Non-raster files (PDF, SVG, GIF, etc.) pass through untouched. Fails OPEN:
 * if compression throws, the original file is uploaded rather than blocking it.
 */
export async function downscaleImageForUpload(
  file: File,
  { maxDimension = 2000, maxSizeMB = 1.5, quality = 0.82 }: DownscaleOptions = {},
): Promise<File> {
  if (!isRasterImage(file)) return file;
  try {
    const compressed = await imageCompression(file, {
      maxWidthOrHeight: maxDimension,
      maxSizeMB,
      initialQuality: quality,
      useWebWorker: true,
    });
    /**
     * browser-image-compression returns a Blob in some envs — normalize to File
     * so callers keep a stable name for the storage path.
     */
    return compressed instanceof File
      ? compressed
      : new File([compressed], file.name, { type: compressed.type || file.type });
  } catch {
    return file; /** never block an upload on optimization failure */
  }
}

/**
 * Returns a browser-displayable image File: HEIC/HEIF is converted to JPEG
 * (renamed `.jpg`); anything else passes through untouched.
 *
 * Throws a user-friendly Error when conversion fails — failing CLOSED is
 * deliberate: silently storing an undisplayable file is exactly the bug this
 * exists to fix.
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  /** 1. HEIC/HEIF → JPEG (fail CLOSED: storing an undisplayable file is the bug). */
  let normalized = file;
  if (isHeicFile(file)) {
    try {
      const { default: heic2any } = await import("heic2any");
      const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
      const blob = Array.isArray(converted) ? converted[0] : converted;
      const jpegName = file.name.replace(HEIC_NAME_RE, "") + ".jpg";
      normalized = new File([blob as Blob], jpegName, { type: "image/jpeg" });
    } catch {
      throw new Error(
        "This photo is in HEIC format and couldn't be converted. Please upload it as JPG or PNG instead.",
      );
    }
  }

  /** 2. Cap resolution (fail OPEN: optimization is best-effort, never blocking). */
  return downscaleImageForUpload(normalized);
}
